import { action, autorun, computed, observable, toJS } from 'mobx';

import {
  EnrollmentSteps,
  ConnectedBankAccountDetails,
  StatusCodes,
  ConnectionStatus,
} from 'components/dutchie-pay/constants';
import { trackDutchiePayEnrollment } from 'src/components/dutchie-pay/utils/analytics-utils';
import { GqlBankAccount, StatusTypes } from 'types/graphql';
import { Nullish } from 'shared/utils/type-utils';

export type MxBankAccount = {
  accountAlias: string;
  accountGuid: string;
  availableBalance: number;
  bankName: string;
  lastDigits: string;
};

export type MxMemberDetails = {
  needsAccountSelection: boolean;
  needsMfa: boolean;
  url: string;
  accounts?: MxBankAccount[];
};

export type AddressProps = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
};

export default class DutchiePayEnrollment {
  @observable activateBankAccount = false;
  @observable address: AddressProps | Nullish | Record<string, never> = {};
  @observable aggregator = `mx`;
  @observable bankAccounts: GqlBankAccount[] | Nullish = [];
  @observable connectedBankAccountDetails: ConnectedBankAccountDetails | null = null;
  @observable connectionStatus = '';
  @observable currentStep: EnrollmentSteps | '' = '';
  @observable currentStepNumber = 1;
  @observable enrollmentStatus: StatusTypes = StatusTypes.statusUnenrolled;
  @observable isExistingUser = false;
  @observable mxBillingAddressId = '';
  @observable mxMemberDetails: MxMemberDetails | Nullish | Record<string, never> = {};
  @observable mxMemberGuid = ''; // Add to mxMemberDetails and mutate when received?
  @observable mxMemberNeedsVerification = false;
  @observable mxSelectedAccountGuid = '';
  @observable mxWidgetUrl = '';
  @observable refUrl = `/checkout`;
  @observable userAcceptance = false;
  @observable userEmail = '';

  constructor() {
    autorun(() => {
      if (this.currentStep && this.currentStep !== EnrollmentSteps.completeEnrollment) {
        trackDutchiePayEnrollment({ step: this.currentStep, action: 'Viewed' });
      }
    });
  }

  @action
  setCurrentStep(step: EnrollmentSteps): void {
    this.currentStep = step;
  }

  @action
  incrementStepNumber(): void {
    this.currentStepNumber += 1;
  }

  @action
  decrementStepNumber(): void {
    this.currentStepNumber -= 1;
  }

  @action
  resetEnrollment(): void {
    this.activateBankAccount = false;
    this.address = {};
    this.bankAccounts = [];
    this.connectionStatus = '';
    this.currentStep = '';
    this.currentStepNumber = 1;
    this.enrollmentStatus = StatusTypes.statusUnenrolled;
    this.isExistingUser = false;
    this.mxBillingAddressId = '';
    this.mxMemberDetails = {};
    this.mxMemberGuid = '';
    this.mxMemberNeedsVerification = false;
    this.mxSelectedAccountGuid = '';
    this.mxWidgetUrl = '';
    this.userAcceptance = false;
    this.userEmail = '';

    this.setRefUrl(this.refUrl);
  }

  @action
  cancelEnrollment(): void {
    this.resetEnrollment();
  }

  @action
  enrollmentStatusReceived(enrollmentStatus): void {
    this.enrollmentStatus = enrollmentStatus?.enrollment?.status;
    this.bankAccounts = enrollmentStatus?.bankAccounts;

    if (enrollmentStatus?.enrollment?.status === StatusTypes.statusEnrolled) {
      this.currentStep = EnrollmentSteps.completeEnrollment;
    }
  }

  @action
  setRefUrl(refUrl: string): void {
    const isDutchiePlusMatch = (): boolean => new RegExp(`/checkouts/`).test(refUrl);
    this.refUrl = isDutchiePlusMatch() ? refUrl : `/checkout`;
  }

  @action
  beginEnrollment(userExists, refUrl = ''): void {
    this.setRefUrl(refUrl);

    if (userExists) {
      this.currentStep = EnrollmentSteps.addressForm;
      this.currentStepNumber = 1;
    } else {
      this.resetEnrollment();
      this.currentStep = EnrollmentSteps.emailForm;
    }
  }

  @action setExistingUser(status: boolean): void {
    this.isExistingUser = status;
  }

  @action
  emailStepCompleted(email: string): void {
    this.userEmail = email;
    this.currentStep = this.isExistingUser ? EnrollmentSteps.passwordForm : EnrollmentSteps.signUpForm;
    trackDutchiePayEnrollment({ step: EnrollmentSteps.emailForm, action: 'Submitted' });
  }

  @action
  getConnectionStatusError(connectionStatus: string): string {
    switch (connectionStatus) {
      case StatusCodes.denied:
        this.connectionStatus = ConnectionStatus.denied;
        break;
      case StatusCodes.challenged:
        this.connectionStatus = ConnectionStatus.challenged;
        break;
      case StatusCodes.failed:
        this.connectionStatus = ConnectionStatus.failed;
        break;
      case StatusCodes.locked:
        this.connectionStatus = ConnectionStatus.locked;
        break;
      case StatusCodes.impeded:
        this.connectionStatus = ConnectionStatus.impeded;
        break;
      case StatusCodes.discontinued:
        this.connectionStatus = ConnectionStatus.discontinued;
        break;
      case StatusCodes.closed:
        this.connectionStatus = ConnectionStatus.closed;
        break;
      default:
        this.connectionStatus = ConnectionStatus.denied;
    }
    return this.connectionStatus;
  }

  @action
  passwordStepCompleted(): void {
    if (this.enrollmentStatus !== StatusTypes.statusEnrolled) {
      this.currentStep = EnrollmentSteps.addressForm;
    }
    trackDutchiePayEnrollment({ step: EnrollmentSteps.passwordForm, action: 'Submitted' });
  }

  @action
  signUpStepCompleted(): void {
    this.currentStep = EnrollmentSteps.addressForm;
    this.incrementStepNumber();
    trackDutchiePayEnrollment({ step: EnrollmentSteps.signUpForm, action: 'Submitted' });
  }

  @action
  addressStepCompleted({ mxWidgetUrl, billingAddressId }): void {
    this.mxWidgetUrl = mxWidgetUrl;
    this.mxBillingAddressId = billingAddressId;
    this.currentStep = EnrollmentSteps.connectBank;
    this.incrementStepNumber();
    trackDutchiePayEnrollment({ step: EnrollmentSteps.addressForm, action: 'Submitted' });
  }

  @action
  reconnectBankAccount({ mxWidgetUrl, billingAddressId }): void {
    this.mxWidgetUrl = mxWidgetUrl;
    this.mxBillingAddressId = billingAddressId;
    this.currentStep = EnrollmentSteps.connectBank;
  }

  @action
  setAddress(address: AddressProps): void {
    this.address = address;
  }

  @action
  mxMemberDetailsReceived(memberDetails: MxMemberDetails): void {
    this.mxMemberDetails = memberDetails;
    this.mxAccountSelected(memberDetails.accounts?.[0]?.accountGuid ?? '');

    if (memberDetails.needsMfa) {
      this.currentStep = EnrollmentSteps.connectBank;
    }

    if (!memberDetails.needsMfa && memberDetails.needsAccountSelection) {
      this.currentStep = EnrollmentSteps.bankAccountSelector;
      this.incrementStepNumber();
    }

    if (!memberDetails.needsMfa && !memberDetails.needsAccountSelection) {
      this.currentStep = EnrollmentSteps.terms;
      this.incrementStepNumber();
    }
  }

  @action
  clearMemberDetails(): void {
    this.mxMemberDetails = {};
  }

  @action
  mxMemberCredentialsVerified(memberGuid: string): void {
    this.mxMemberGuid = memberGuid;
  }

  @action
  mxBankConnectionCompleted(): void {
    if (this.mxMemberDetails?.accounts && this.mxMemberDetails.accounts.length > 1) {
      this.currentStep = EnrollmentSteps.bankAccountSelector;
    } else if (this.mxMemberDetails?.url && this.mxMemberDetails.accounts?.length === 1) {
      this.currentStep = EnrollmentSteps.terms;
    } else {
      this.currentStep = EnrollmentSteps.bankAccountsLoading;
      this.mxMemberNeedsVerification = true;
    }
    trackDutchiePayEnrollment({ step: EnrollmentSteps.connectBank, action: 'Submitted' });
  }

  @action
  mxAccountSelected(accountGuid: string): void {
    this.mxSelectedAccountGuid = accountGuid;
  }

  @computed
  get mxMfaUrl(): string {
    return this.mxMemberDetails?.url ?? '';
  }

  @computed
  get mxBankAccounts(): MxBankAccount[] | Nullish {
    return toJS(this.mxMemberDetails?.accounts) ?? [];
  }

  @action
  createBankAccountRequested(): void {
    this.currentStep = EnrollmentSteps.tosLoading;
    trackDutchiePayEnrollment({ step: EnrollmentSteps.bankAccountSelector, action: 'Submitted' });
  }

  @action
  createBankAccountCompleted(): void {
    this.currentStep = EnrollmentSteps.terms;
    this.incrementStepNumber();
  }
  @action
  setConnectedBankAccountDetails(connectedBankAccount: ConnectedBankAccountDetails): void {
    this.connectedBankAccountDetails = connectedBankAccount;
  }

  @action
  termsOfServiceError(): void {
    this.currentStep = EnrollmentSteps.terms;
  }

  @action
  editBankAccount(): void {
    this.currentStep = EnrollmentSteps.addressForm;
    this.currentStepNumber = 1;
  }

  @action
  userAcceptanceRequested(): void {
    this.currentStep = EnrollmentSteps.tosLoading;
  }

  @action
  activateBankAccountRequested(): void {
    this.currentStep = EnrollmentSteps.tosLoading;
  }

  @action
  userAcceptanceSucceeded(): void {
    this.userAcceptance = true;
    this.currentStep = EnrollmentSteps.completeEnrollment;
    trackDutchiePayEnrollment({ step: EnrollmentSteps.completeEnrollment, action: 'Succeeded' });
  }

  activateBankAccountSucceeded(): void {
    this.activateBankAccount = true;
    this.currentStep = EnrollmentSteps.completeEnrollment;
    trackDutchiePayEnrollment({ step: EnrollmentSteps.completeEnrollment, action: 'Succeeded' });
  }

  @action resetPasswordClicked(): void {
    this.currentStep = EnrollmentSteps.forgotPasswordForm;
  }

  @action resetPasswordSucceeded(): void {
    this.currentStep = EnrollmentSteps.passwordForm;
  }
}
